import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProjectsByType } from './services/projectService';
import useScrollAnimation from './hooks/useScrollAnimation';
import useParallax from './hooks/useParallax';
import Loader from './components/Loader';
import './commercial.css';
import './animations.css';

// Separate component for animated project items
function AnimatedProject({ project }) {
  const [projectRef, projectVisible] = useScrollAnimation(0.2);
  
  // Reverse parallax for commercial projects (makes them "pop" forward)
  const [projectParallaxRef, projectParallaxStyle] = useParallax({ strength: -0.25, max: 18, disableOpacity: true });
  
  return (
    <div className="btw-project" ref={projectRef} style={{borderTop: '1px solid #baa170'}}>
      <div className={`btw-project-header project-header-animate ${projectVisible ? 'visible' : ''}`}>
        <div className="btw-commercial-project-name">{project.name.toUpperCase()}</div>
        <div className="btw-commercial-project-type">{project.type.toUpperCase()}</div>
      </div>
      <div className={`btw-project-image project-image-animate ${projectVisible ? 'visible' : ''}`} ref={projectParallaxRef} style={projectParallaxStyle}>
        <Link to={`/project/${project.id}`}>
          {(project.mainImage || project.mainImagePreview) ? (
            <img 
              src={project.mainImage || project.mainImagePreview} 
              alt={project.name} 
            />
          ) : (
            <div style={{
              width: '100%',
              height: '300px',
              background: '#23221e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#888',
              fontSize: '0.9rem'
            }}>
              No image available
            </div>
          )}
        </Link>
      </div>
    </div>
  );
}

export default function Commercial() {
  const [firebaseProjects, setFirebaseProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heroRef, heroVisible] = useScrollAnimation(0.3);
  
  // Parallax effect for hero background
  const [heroParallaxRef, heroParallaxStyle] = useParallax({ strength: 0.3, max: 25, disableOpacity: true });

  useEffect(() => {
    async function loadCommercialProjects() {
      try {
        setLoading(true);
        const commercialProjects = await getProjectsByType('commercial');
        setFirebaseProjects(commercialProjects);
      } catch (err) {
        console.error('Error loading commercial projects:', err);
        setError('Failed to load new projects');
      } finally {
        setLoading(false);
      }
    }

    loadCommercialProjects();
  }, []);

  // Use only commercial projects
  const allProjects = firebaseProjects;

  return (
    <main>
      {/* Hero above projects */}
      <section className="btw-commercial-hero" ref={heroRef}>
        <div className="container" style={{maxWidth: '1200px', margin: '0 auto'}}>
          <div className="btw-commercial-hero-content" ref={heroParallaxRef} style={heroParallaxStyle}>
            <div className={`btw-commercial-hero-subtitle subtitle-animate ${heroVisible ? 'visible' : ''}`} style={{fontSize: '1.1rem'}}>
              SEE FOR YOURSELF
            </div>
            <h1 className={`btw-commercial-hero-title hero-title-animate ${heroVisible ? 'visible' : ''}`}>
              The Work That<br />Defines Us
            </h1>
            <p className={`btw-commercial-hero-desc description-animate ${heroVisible ? 'visible' : ''}`}>
              A showcase of our finest projects<br />that transform ideas into iconic spaces.
            </p>
          </div>
        </div>
      </section>

      {/* Project stack */}
      <section className="btw-project-stack" style={{paddingTop: '32px'}}>
        {loading && firebaseProjects.length === 0 && (
          <div style={{ padding: '40px 0' }}>
            <Loader 
              variant="blueprint" 
              category="projects"
              message={{
                text: "Loading Projects",
                subtitle: "Curating our finest commercial and residential spaces"
              }}
            />
          </div>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#f44336', fontSize: '0.9rem' }}>
            {error}
          </div>
        )}

        {allProjects.map((project) => (
          <AnimatedProject key={project.id} project={project} />
        ))}
      </section>

      {/* Get Started section */}
      <section className="btw-getstarted-section bg-neutral-500 min-h-[60vh] flex flex-col justify-center items-center text-center">
        <div className="btw-getstarted-inner">
          <div className="text-neutral-400 font-mono mb-6" style={{fontSize: '0.8rem'}}>GET STARTED</div>
          <h1 className="text-5xl md:text-7xl font-bold text-[#f7f5ec] mb-8 leading-tight">OUR NEXT PROJECT<br />COULD BE YOURS</h1>
          <Link to="/letstalk" style={{textDecoration: 'none'}}>
            <button className="btw-cta" type="button"><span>LET'S TALK</span></button>
          </Link>
        </div>
      </section>
    </main>
  );
}
